import { useEffect, useMemo, useState } from "react";
import { ApiClientError, applyAmazonKwRank, fetchAmazonKwRank, runAmazonKwRank } from "../api/client";
import { ConfirmDialog } from "./ConfirmDialog";
import { StateBanner } from "./StateBanner";
import { yen } from "../lib/meta";
import type {
  AmazonKwRankApplyResponse,
  AmazonKwRankDailyPoint,
  AmazonKwRankKeyword,
  AmazonKwRankProposal,
  AmazonKwRankResponse,
} from "../types/contract";

/**
 * Amazon タブ「KW 順位」パネル（2026-11-05・設計 amazon/reports/2026-11-03_day1-review.md §8-4 → amazon/kw-rank-tracker-2026-11-05.md）。
 *   監視 KW 一覧（自然順位の最良 ASIN／広告順位／TOS IS／入札／上部加算／7日ACOS／前日比／提案数）
 *   → 行クリックで詳細: 30 日推移（自然・広告・TOS IS）＋ASIN 別順位＋広告行＋競合上位 5＋提案行。
 *   提案の「承認して反映」は確認ダイアログ → POST /amazon-kw-rank/apply（worker が Ads API に反映→読み戻し→台帳）。
 *   「試走（変更しない）」を ON にすると dry_run=true で現在値の読み取りと台帳記録だけ行う。自動変更はしない。
 *   自然順位は Apify（1 日 1 回・JST 7 時台）、TOS IS は Ads API（前日確定）。当日分の TOS IS は出ない。
 */

type FetchState = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; data: AmazonKwRankResponse };

interface Props {
  range: { from: string; to: string };
}

const DASH = "—";
const pct1 = (n: number | null): string => (n === null ? DASH : `${n.toFixed(n < 1 ? 2 : 1)}%`);
const rankText = (n: number | null): string => (n === null ? DASH : `${n}位`);

function delta(cur: number | null, prev: number | null, lowerIsBetter: boolean): { text: string; cls: string } | null {
  if (cur === null || prev === null) return null;
  const d = cur - prev;
  if (d === 0) return { text: "±0", cls: "" };
  const better = lowerIsBetter ? d < 0 : d > 0;
  return { text: `${d > 0 ? "+" : "−"}${Math.abs(d).toFixed(Number.isInteger(d) ? 0 : 1)}`, cls: better ? "up" : "down" };
}

function sourceBadge(source: string | null) {
  if (source === "provider") return <span className="badge" style={{ background: "var(--warn-bg)", color: "var(--warn)" }} title="Amazon が Bot 確認を返したため代替経由で読んだ（広告順位は空）">代替経由</span>;
  return null;
}

/** 30 日推移の軽量 SVG（自然順位・広告順位は上が 1 位・TOS IS は右軸 0〜100%）。 */
function TrendChart({ points }: { points: AmazonKwRankDailyPoint[] }) {
  const W = 640;
  const H = 180;
  const padL = 34;
  const padR = 34;
  const padT = 10;
  const padB = 24;
  const n = points.length;
  if (n === 0) return <div className="kwr-empty">推移データがまだありません。</div>;
  const ranks = points.flatMap((p) => [p.organic_best, p.sponsored_best]).filter((v): v is number => v !== null);
  const maxRank = Math.max(10, ...ranks);
  const x = (i: number) => padL + (n === 1 ? (W - padL - padR) / 2 : ((W - padL - padR) * i) / (n - 1));
  const yRank = (r: number) => padT + ((H - padT - padB) * (r - 1)) / Math.max(1, maxRank - 1);
  const yTos = (t: number) => padT + (H - padT - padB) * (1 - Math.min(100, t) / 100);
  const path = (sel: (p: AmazonKwRankDailyPoint) => number | null, y: (v: number) => number) => {
    let d = "";
    let pen = false;
    points.forEach((p, i) => {
      const v = sel(p);
      if (v === null) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
      pen = true;
    });
    return d;
  };
  const dots = (sel: (p: AmazonKwRankDailyPoint) => number | null, y: (v: number) => number, color: string) =>
    points.map((p, i) => {
      const v = sel(p);
      return v === null ? null : <circle key={`${color}${i}`} cx={x(i)} cy={y(v)} r={2.5} fill={color} />;
    });
  const tickEvery = Math.max(1, Math.ceil(n / 6));
  return (
    <svg className="kwr-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="自然順位・広告順位・TOS IS の推移">
      {[1, Math.round(maxRank / 2), maxRank].map((r) => (
        <g key={`r${r}`}>
          <line x1={padL} x2={W - padR} y1={yRank(r)} y2={yRank(r)} stroke="var(--border)" strokeWidth={1} />
          <text x={padL - 4} y={yRank(r) + 3.5} fontSize={10} textAnchor="end" fill="var(--ink-sub)">
            {r}位
          </text>
        </g>
      ))}
      {[0, 50, 100].map((t) => (
        <text key={`t${t}`} x={W - padR + 4} y={yTos(t) + 3.5} fontSize={10} fill="var(--ink-sub)">
          {t}%
        </text>
      ))}
      {points.map((p, i) =>
        i % tickEvery === 0 || i === n - 1 ? (
          <text key={p.date} x={x(i)} y={H - 6} fontSize={10} textAnchor="middle" fill="var(--ink-sub)">
            {p.date.slice(5)}
          </text>
        ) : null
      )}
      <path d={path((p) => p.tos_is, yTos)} fill="none" stroke="var(--warn)" strokeWidth={2} strokeDasharray="4 3" />
      <path d={path((p) => p.sponsored_best, yRank)} fill="none" stroke="#2f7a4a" strokeWidth={2} />
      <path d={path((p) => p.organic_best, yRank)} fill="none" stroke="var(--accent)" strokeWidth={2.5} />
      {dots((p) => p.tos_is, yTos, "var(--warn)")}
      {dots((p) => p.sponsored_best, yRank, "#2f7a4a")}
      {dots((p) => p.organic_best, yRank, "var(--accent)")}
    </svg>
  );
}

export function AmazonKwRankPanel({ range }: Props) {
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [openKw, setOpenKw] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [pending, setPending] = useState<AmazonKwRankProposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [runBusy, setRunBusy] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);

  const load = () => {
    setState({ status: "loading" });
    fetchAmazonKwRank(range.from, range.to)
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => setState({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  const data = state.status === "ok" ? state.data : null;
  const proposalTotal = useMemo(() => (data ? data.keywords.reduce((s, k) => s + k.proposals.length, 0) : 0), [data]);
  const current = data?.keywords.find((k) => k.keyword === openKw) ?? null;

  const runApply = async (reason?: string) => {
    if (!pending) return;
    setBusy(true);
    setApplyError(null);
    try {
      const res: AmazonKwRankApplyResponse = await applyAmazonKwRank({
        kind: pending.kind,
        keyword: pending.keyword,
        campaign_id: pending.campaign_id,
        campaign_name: pending.campaign_name,
        ad_group_id: pending.ad_group_id,
        target_id: pending.target_id,
        value: pending.proposed,
        rule: pending.rule,
        reason: [pending.reason, reason].filter(Boolean).join(" / "),
        dry_run: dryRun,
      });
      const unit = pending.kind === "bid" ? "円" : "%";
      setApplyResult({
        ok: true,
        text: `${res.status === "dry_run" ? "【試走】" : "【反映】"}${pending.keyword}／${pending.ad_group_name || pending.campaign_name}: ${res.current ?? "?"}${unit} → ${res.value}${unit}。${res.message}${res.log_id ? `（台帳 #${res.log_id}）` : ""}`,
      });
      setPending(null);
      load();
    } catch (e) {
      setApplyError(e instanceof ApiClientError ? e.message : "反映に失敗しました。");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setRunBusy(true);
    setRunResult(null);
    try {
      const r = await runAmazonKwRank("both");
      const a = r.apify ? (r.apify.started ? `Apify run 開始 (${r.apify.run_id})` : `Apify: ${r.apify.reason ?? "開始せず"}`) : "";
      const t = r.tos ? (r.tos.created ? `TOS レポート作成 (${r.tos.report_id})` : `TOS: ${r.tos.reason ?? "作成せず"}`) : "";
      setRunResult(`${[a, t].filter(Boolean).join(" ／ ")}。取込は 5 分ごとの tick が行います（数分後に再読込）。`);
    } catch (e) {
      setRunResult(e instanceof ApiClientError ? `失敗: ${e.message}` : "失敗しました。");
    } finally {
      setRunBusy(false);
    }
  };

  return (
    <div className="card kwr-card">
      <div className="kwr-head">
        <h3 className="section-title">KW 順位（Amazon 検索）</h3>
        {data && (
          <span className="kwr-meta">
            自然順位 {data.status.latest_rank_date ?? "未取得"}／TOS IS {data.status.latest_tos_date ?? "未取得"}
            {data.status.live === "stale" && "／live は古いキャッシュ"}
            {data.status.live === "unavailable" && "／入札・上部加算は取得不可"}
          </span>
        )}
        {proposalTotal > 0 && <span className="kwr-prop-count" title="提案の件数">{proposalTotal}</span>}
        <span className="spacer" />
        <button className="btn btn-sm" onClick={load} disabled={state.status === "loading"}>
          再読込
        </button>
        <button className="btn btn-sm" onClick={runNow} disabled={runBusy || !data} title={data && !data.status.apify_configured ? "APIFY_TOKEN が未設定のため TOS レポートだけ作成します" : "Apify run と TOS レポートをいま開始する"}>
          {runBusy ? "開始中…" : "今すぐ取得"}
        </button>
      </div>
      <p className="kwr-note">
        自然順位・広告順位は Apify（1 日 1 回・JST 7 時台・最大 3 頁）、検索上部シェア（TOS IS）は Amazon 広告 API の前日確定値。提案は TOS IS と 7 日 ACOS のルール（閾値は cs_settings
        amazon_kw_rank_rules・既定 ACOS 40%）。反映は「承認」ボタンだけで行い、自動では変えません。
      </p>
      {runResult && <div className="kwr-result">{runResult}</div>}
      {applyResult && <div className={`kwr-result ${applyResult.ok ? "" : "error"}`}>{applyResult.text}</div>}

      {state.status === "loading" && <StateBanner kind="loading" message="KW 順位を読み込み中…" />}
      {state.status === "error" && <StateBanner kind="error" message={`KW 順位の取得に失敗しました: ${state.message}`} onRetry={load} />}
      {data && (
        <>
          {data.status.live === "unavailable" && data.status.live_error && <StateBanner kind="missing" message={`入札・上部加算の live 取得に失敗: ${data.status.live_error}`} />}
          {!data.status.apify_configured && <StateBanner kind="missing" message="Apify（APIFY_TOKEN）が未設定のため自然順位は取り込まれません。TOS IS・入札は表示できます。" />}
          <div className="table-wrap">
            <table className="data-table kwr-table">
              <thead>
                <tr>
                  <th>キーワード</th>
                  <th>自然順位（最良）</th>
                  <th>広告順位</th>
                  <th className="num">TOS IS</th>
                  <th className="num">入札</th>
                  <th className="num">上部加算</th>
                  <th className="num">7日ACOS</th>
                  <th className="num">7日費用</th>
                  <th className="num">提案</th>
                </tr>
              </thead>
              <tbody>
                {data.keywords.map((k) => {
                  const spAds = k.ads.filter((a) => a.sp);
                  const bids = spAds.map((a) => a.bid).filter((b): b is number => b !== null);
                  const pls = spAds.map((a) => a.placement_top).filter((p): p is number => p !== null);
                  const spend = k.ads.reduce((s, a) => s + a.spend_7d, 0);
                  const sales = k.ads.reduce((s, a) => s + a.sales_7d, 0);
                  const acos = sales > 0 ? (spend / sales) * 100 : null;
                  const dOrg = delta(k.organic_best.rank, k.prev.organic_best, true);
                  const dSp = delta(k.sponsored_best.rank, k.prev.sponsored_best, true);
                  const active = openKw === k.keyword;
                  return (
                    <tr key={k.keyword} className={`kwr-row ${active ? "active" : ""}`} onClick={() => setOpenKw(active ? null : k.keyword)}>
                      <td>
                        <strong>{k.keyword}</strong> {sourceBadge(k.source)}
                        <div className="cell-sub">{k.latest_date ? `${k.latest_date} 計測` : "未計測"}</div>
                      </td>
                      <td>
                        {rankText(k.organic_best.rank)}
                        {dOrg && <span className={`kwr-delta ${dOrg.cls}`}>{dOrg.text}</span>}
                        {k.organic_best.label && <div className="cell-sub">{k.organic_best.label}{k.organic_best.page ? `・${k.organic_best.page}頁` : ""}</div>}
                      </td>
                      <td>
                        {rankText(k.sponsored_best.rank)}
                        {dSp && <span className={`kwr-delta ${dSp.cls}`}>{dSp.text}</span>}
                        {k.sponsored_best.label && <div className="cell-sub">{k.sponsored_best.label}</div>}
                      </td>
                      <td className="num">{pct1(k.tos_is)}</td>
                      <td className="num">{bids.length ? (bids.length > 1 ? `${yen(Math.min(...bids))}〜${yen(Math.max(...bids))}` : yen(bids[0])) : DASH}</td>
                      <td className="num">{pls.length ? `+${Math.max(...pls)}%` : DASH}</td>
                      <td className="num">{acos === null ? (spend > 0 ? "売上なし" : DASH) : `${acos.toFixed(1)}%`}</td>
                      <td className="num">{spend > 0 ? yen(spend) : DASH}</td>
                      <td className="num">{k.proposals.length > 0 ? <span className="kwr-prop-count">{k.proposals.length}</span> : DASH}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {current && <KeywordDetail kw={current} dryRun={dryRun} setDryRun={setDryRun} onPropose={(p) => { setApplyError(null); setPending(p); }} />}

          {data.runs.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary className="kwr-meta" style={{ cursor: "pointer" }}>
                Apify run 履歴（直近 {data.runs.length} 件・費用）／反映台帳（{data.changes.length} 件）
              </summary>
              <div className="table-wrap" style={{ marginTop: 8 }}>
                <table className="data-table kwr-mini" style={{ minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th>日</th>
                      <th>状態</th>
                      <th className="num">費用 USD</th>
                      <th className="num">rank 行</th>
                      <th className="num">代替経由</th>
                      <th>起動</th>
                      <th>備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.runs.map((r) => (
                      <tr key={r.run_id}>
                        <td>{r.date}</td>
                        <td>{r.status}</td>
                        <td className="num">{r.usage_usd === null ? DASH : r.usage_usd.toFixed(3)}</td>
                        <td className="num">{r.rank_rows ?? DASH}</td>
                        <td className="num">{r.provider_rows ?? DASH}</td>
                        <td>{r.trigger}{r.attempts > 1 ? `（${r.attempts}回目）` : ""}</td>
                        <td className="kwr-ellipsis" title={r.error ?? ""}>{r.error ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.changes.length > 0 && (
                <div className="table-wrap" style={{ marginTop: 8 }}>
                  <table className="data-table kwr-mini" style={{ minWidth: 560 }}>
                    <thead>
                      <tr>
                        <th>日時(UTC)</th>
                        <th>KW</th>
                        <th>種別</th>
                        <th>対象</th>
                        <th className="num">前</th>
                        <th className="num">後</th>
                        <th className="num">読み戻し</th>
                        <th>結果</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.changes.map((c) => (
                        <tr key={c.id}>
                          <td>{c.created_at}</td>
                          <td>{c.keyword}</td>
                          <td>{c.kind === "bid" ? "入札" : "上部加算"}</td>
                          <td className="kwr-ellipsis" title={c.campaign_name}>{c.campaign_name || c.ad_group_id}</td>
                          <td className="num">{c.before_value ?? DASH}</td>
                          <td className="num">{c.after_value ?? DASH}</td>
                          <td className="num">{c.read_back ?? DASH}</td>
                          <td>{c.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </details>
          )}
        </>
      )}

      {pending && (
        <ConfirmDialog
          title={dryRun ? "試走（Amazon は変更しません）" : "Amazon 広告に反映します"}
          description={`${pending.keyword}／${pending.ad_group_name || pending.campaign_name}（${pending.match_type}）: ${pending.kind === "bid" ? "入札" : "検索上部の加算"} ${pending.current ?? "?"}${pending.kind === "bid" ? "円" : "%"} → ${pending.proposed}${pending.kind === "bid" ? "円" : "%"}\n根拠: ${pending.reason}${dryRun ? "\n\n試走: 現在値を読んで台帳に dry_run として記録するだけです。" : "\n\n反映後に読み戻して一致を確認し、台帳（amazon_ads_change_log）に記録します。"}`}
          confirmLabel={dryRun ? "試走する" : "承認して反映"}
          danger={!dryRun}
          requireReason={false}
          busy={busy}
          errorMessage={applyError}
          onConfirm={(reason) => void runApply(reason)}
          onCancel={() => {
            if (!busy) setPending(null);
          }}
        />
      )}
    </div>
  );
}

function KeywordDetail({
  kw,
  dryRun,
  setDryRun,
  onPropose,
}: {
  kw: AmazonKwRankKeyword;
  dryRun: boolean;
  setDryRun: (v: boolean) => void;
  onPropose: (p: AmazonKwRankProposal) => void;
}) {
  const foundAsins = kw.asins.filter((a) => a.found);
  return (
    <div className="kwr-detail">
      <div className="kwr-grid">
        <div>
          <h4>
            {kw.keyword} の推移（{kw.daily.length} 日）
          </h4>
          <div className="kwr-chart-wrap">
            <TrendChart points={kw.daily} />
          </div>
          <div className="kwr-legend">
            <span>
              <i style={{ background: "var(--accent)" }} />
              自然順位（最良 ASIN・左軸）
            </span>
            <span>
              <i style={{ background: "#2f7a4a" }} />
              広告順位（目視・左軸）
            </span>
            <span>
              <i style={{ background: "var(--warn)" }} />
              TOS IS（検索上部シェア・右軸・前日確定）
            </span>
          </div>

          <h4>ASIN 別（{kw.latest_date ?? "未計測"}）</h4>
          {foundAsins.length === 0 ? (
            <div className="kwr-empty">読んだ範囲（最大 3 頁）に当社 ASIN はありませんでした。</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table kwr-mini" style={{ minWidth: 420 }}>
                <thead>
                  <tr>
                    <th>商品</th>
                    <th className="num">自然</th>
                    <th className="num">頁</th>
                    <th className="num">広告</th>
                    <th className="num">表示位置</th>
                  </tr>
                </thead>
                <tbody>
                  {foundAsins.map((a) => (
                    <tr key={a.asin}>
                      <td>
                        {a.label} {sourceBadge(a.source)}
                        <div className="cell-sub">{a.asin}</div>
                      </td>
                      <td className="num">{rankText(a.organic_rank)}</td>
                      <td className="num">{a.organic_page ?? DASH}</td>
                      <td className="num">{rankText(a.sponsored_rank)}</td>
                      <td className="num">{a.position ?? DASH}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4>
            競合上位{kw.results_text ? `（${kw.results_text}・広告枠 ${kw.ads_shown ?? "?"}）` : ""}
          </h4>
          {kw.competition.length === 0 ? (
            <div className="kwr-empty">まだありません。</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table kwr-mini" style={{ minWidth: 420 }}>
                <thead>
                  <tr>
                    <th className="num">#</th>
                    <th>商品</th>
                    <th className="num">価格</th>
                    <th className="num">★</th>
                    <th className="num">レビュー</th>
                  </tr>
                </thead>
                <tbody>
                  {kw.competition.map((c, i) => (
                    <tr key={String(c.asin ?? i)}>
                      <td className="num">{String(c.organicRank ?? i + 1)}</td>
                      <td>
                        <div className="kwr-ellipsis" title={String(c.title ?? "")}>{String(c.title ?? c.asin ?? "")}</div>
                        <div className="cell-sub">{String(c.asin ?? "")}{c.sponsored ? "・広告" : ""}</div>
                      </td>
                      <td className="num">{typeof c.price === "number" ? yen(c.price) : DASH}</td>
                      <td className="num">{typeof c.rating === "number" ? c.rating.toFixed(1) : DASH}</td>
                      <td className="num">{typeof c.reviewCount === "number" ? c.reviewCount.toLocaleString("ja-JP") : DASH}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <h4>広告（このKWを狙う広告グループ）</h4>
          {kw.ads.length === 0 ? (
            <div className="kwr-empty">このKWを狙う広告はありません。</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table kwr-mini" style={{ minWidth: 480 }}>
                <thead>
                  <tr>
                    <th>グループ</th>
                    <th className="num">TOS IS</th>
                    <th className="num">入札</th>
                    <th className="num">上部</th>
                    <th className="num">7日費用</th>
                    <th className="num">7日売上</th>
                    <th className="num">ACOS</th>
                  </tr>
                </thead>
                <tbody>
                  {kw.ads.map((a) => {
                    const dT = delta(a.tos_is, a.tos_is_prev, false);
                    return (
                      <tr key={`${a.ad_group_id}:${a.match_type}`}>
                        <td>
                          <div className="kwr-ellipsis" title={a.campaign_name}>
                            {a.ad_group_name || a.ad_group_id}
                          </div>
                          <div className="cell-sub">
                            {a.sp ? "SP" : "SB/SD"}・{a.match_type}
                            {a.target_state && a.target_state !== "ENABLED" ? `・${a.target_state}` : ""}
                            {!a.operable && a.sp ? "・許可外" : ""}
                          </div>
                        </td>
                        <td className="num">
                          {pct1(a.tos_is)}
                          {dT && <span className={`kwr-delta ${dT.cls}`}>{dT.text}</span>}
                          {a.tos_is_date && <div className="cell-sub">{a.tos_is_date.slice(5)}</div>}
                        </td>
                        <td className="num">
                          {a.bid === null ? DASH : yen(a.bid)}
                          {a.bid_source === "report" && <div className="cell-sub">レポート値</div>}
                        </td>
                        <td className="num">{a.placement_top === null ? DASH : `+${a.placement_top}%`}</td>
                        <td className="num">{yen(a.spend_7d)}</td>
                        <td className="num">{a.sales_7d > 0 ? yen(a.sales_7d) : DASH}</td>
                        <td className="num">{a.acos_7d === null ? (a.spend_7d > 0 ? "売上なし" : DASH) : `${a.acos_7d.toFixed(1)}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <h4>提案（{kw.proposals.length}）</h4>
          <div className="kwr-note" style={{ marginBottom: 8 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              試走（変更しない・現在値を読んで台帳に残すだけ）
            </label>
          </div>
          {kw.proposals.length === 0 ? (
            <div className="kwr-empty">いまのルールに当たる提案はありません（TOS IS が未確定・表示回数不足・SB/SD は対象外）。</div>
          ) : (
            kw.proposals.map((p) => (
              <div key={p.id} className={`kwr-proposal ${p.rule === "pull_bid" ? "pull" : "push"}`}>
                <div className="title">
                  {p.kind === "bid" ? "入札" : "検索上部の加算"}: {p.current ?? "?"}
                  {p.kind === "bid" ? "円" : "%"} → <strong>{p.proposed}{p.kind === "bid" ? "円" : "%"}</strong>
                  <span className="cell-sub" style={{ display: "block", fontWeight: 400 }}>
                    {p.ad_group_name || p.campaign_name}（{p.match_type}）
                  </span>
                </div>
                <div className="reason">{p.reason}</div>
                <div className="actions">
                  <button className={`btn btn-sm ${dryRun ? "" : "btn-primary"}`} disabled={!p.applicable} onClick={() => onPropose(p)} title={p.applicable ? "" : "targetId 不明か操作許可の範囲外のため反映できません"}>
                    {dryRun ? "試走する" : "承認して反映"}
                  </button>
                  {!p.applicable && <span className="kwr-meta">反映不可（targetId 不明／許可外）</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

